import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FileText, Receipt, GitCompare, Lock } from "lucide-react";
import { runsApi, workspacesApi } from "../api/endpoints";
import { SkeletonTable } from "../components/states/Skeleton";
import { ErrorState } from "../components/states/ErrorState";
import { EmptyState } from "../components/states/EmptyState";
import { formatDateTime, shortHash } from "../lib/format";
import { cn } from "../lib/utils";

/**
 * Phase 6f — Reports index.
 * Phase 7b — Recovery Assurance report entitlement gating.
 *
 * Trial workspaces see the button disabled with an inline explanation of
 * which plans unlock the full report. The Audit Receipt remains free on
 * every plan. Server-side is the actual gate — this UI is a transparent
 * mirror, not a security boundary.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const DEMO_MAILTO =
  "mailto:hello@recoveryassure.com?subject=Upgrade%20request%20%E2%80%94%20RecoveryAssure";
const REQUIRED_PLANS = ["professional", "enterprise"];

export default function ReportsPage() {
  const { workspaceId } = useParams();
  const [runs, setRuns] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [error, setError] = useState(null);
  const [compareByRun, setCompareByRun] = useState({});

  const load = () => {
    setError(null);
    setRuns(null);
    setWorkspace(null);
    Promise.all([
      runsApi.list(workspaceId),
      workspacesApi.get(workspaceId),
    ])
      .then(([rs, ws]) => {
        setRuns(rs);
        setWorkspace(ws);
      })
      .catch(setError);
  };
  useEffect(load, [workspaceId]);

  const completed = useMemo(
    () => (runs || []).filter((r) => r.status === "completed"),
    [runs],
  );
  const currentPlan = workspace?.plan || "trial";
  const isEntitled = REQUIRED_PLANS.includes(currentPlan);

  if (error) return <ErrorState error={error} onRetry={load} />;
  if (runs === null || workspace === null) return <SkeletonTable rows={6} cols={4} />;

  return (
    <div className="space-y-5" data-testid="reports-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-slate-500">
          Workspace · plan {currentPlan}
        </div>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900" data-testid="reports-title">
          Reports
        </h1>
        <p className="mt-1 text-sm text-slate-600 max-w-2xl">
          Server-rendered, deterministic PDF artefacts for each analysis
          run. Report render never invokes an LLM — AI content in the
          report is drawn from the cache only.
        </p>
      </header>

      {!isEntitled && (
        <div
          data-testid="reports-entitlement-banner"
          role="note"
          className="rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex flex-wrap items-start justify-between gap-3"
        >
          <div className="flex gap-3 items-start">
            <Lock size={16} aria-hidden="true" className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">
                The full Recovery Assurance Report is available on Professional and Enterprise plans.
              </div>
              <p className="mt-1 leading-relaxed">
                The Audit Receipt remains available on all plans, including
                this trial workspace.
              </p>
            </div>
          </div>
          <a
            href={DEMO_MAILTO}
            data-testid="reports-entitlement-contact"
            className="inline-flex items-center gap-1.5 rounded bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
          >
            Contact us
          </a>
        </div>
      )}

      {completed.length === 0 ? (
        <EmptyState
          title="No completed analyses yet"
          description="Run an analysis from the Analyses page to make artefacts available here."
        />
      ) : (
        <div className="rounded border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reports-table">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Run</th>
                  <th className="px-4 py-2.5">Created</th>
                  <th className="px-4 py-2.5">Rule library</th>
                  <th className="px-4 py-2.5">Audit receipt</th>
                  <th className="px-4 py-2.5">Recovery Assurance Report</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {completed.map((r, idx) => {
                  const previousRun = completed[idx + 1]; // list is created-desc
                  const compareFrom = compareByRun[r.id];
                  const reportHref = compareFrom
                    ? `${BACKEND}/api/workspaces/${workspaceId}/runs/${r.id}/recovery_assurance_report.pdf?compare_from=${compareFrom}`
                    : `${BACKEND}/api/workspaces/${workspaceId}/runs/${r.id}/recovery_assurance_report.pdf`;
                  return (
                    <tr
                      key={r.id}
                      data-testid={`report-row-${r.id}`}
                      className="align-top"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/w/${workspaceId}/analyses/${r.id}`}
                          className="font-medium text-slate-900 hover:underline"
                        >
                          {r.name}
                        </Link>
                        <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                          {shortHash(r.id)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {formatDateTime(r.created_at)}
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-[11px]">
                        v{r.rule_library_version || "?"} · {shortHash(r.rule_library_hash || "")}
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={`${BACKEND}/api/workspaces/${workspaceId}/runs/${r.id}/audit_receipt.pdf`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`report-download-audit-${r.id}`}
                          className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-800 hover:bg-slate-100"
                        >
                          <Receipt size={13} /> Audit receipt
                        </a>
                      </td>
                      <td className="px-4 py-3 space-y-2">
                        {isEntitled ? (
                          <a
                            href={reportHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`report-download-recovery-${r.id}`}
                            className="inline-flex items-center gap-1.5 rounded bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-800"
                          >
                            <FileText size={13} /> Recovery Assurance
                          </a>
                        ) : (
                          <button
                            type="button"
                            disabled
                            data-testid={`report-download-recovery-${r.id}`}
                            data-locked="true"
                            title="Available on Professional and Enterprise plans"
                            className="inline-flex items-center gap-1.5 rounded bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-500 cursor-not-allowed"
                          >
                            <Lock size={11} /> Recovery Assurance
                          </button>
                        )}
                        {previousRun && isEntitled && (
                          <label
                            className={cn(
                              "flex items-center gap-2 text-[11px] text-slate-600",
                            )}
                          >
                            <input
                              type="checkbox"
                              data-testid={`report-compare-toggle-${r.id}`}
                              checked={Boolean(compareFrom)}
                              onChange={(e) =>
                                setCompareByRun((prev) => ({
                                  ...prev,
                                  [r.id]: e.target.checked ? previousRun.id : undefined,
                                }))
                              }
                            />
                            <GitCompare size={11} className="text-slate-400" />
                            Include comparison with {previousRun.name}
                          </label>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
